import { useMemo } from 'react';
import type { RegimeProfileDimensionResult, RegimeProfileSliceMetrics, RegimeProfileThresholds } from '@/types/appRouter';
import { ExpandableDataTable, StatList, StatRow, Tooltip } from '@/components/UI';
import type { ColumnDef, Row } from '@/components/UI/dataTableConfig';
import {
    buildDivergingBarScale,
    buildPrimarySliceRows,
    formatInt,
    formatShare,
    formatSignedPercent,
    getConcentrationFlags,
    type DivergingBarScale,
    type PrimarySliceRow
} from './regimeProfilePresentation';

function formatNullableShare(value: number | null): string {
    return value === null ? '—' : formatShare(value, 1);
}

/** Full metric set behind a row expansion, with the three concentration shares flagged. */
function SliceDetail({ slice, thresholds }: { slice: RegimeProfileSliceMetrics; thresholds: RegimeProfileThresholds }) {
    const concentrationFlags = getConcentrationFlags(slice, thresholds);

    return (
        <div className="grid gap-x-10 gap-y-4 p-4 md:grid-cols-3">
            <StatList>
                <StatRow label="Winsorized mean" value={formatSignedPercent(slice.meanProfitPercent)} />
                <StatRow label="Raw mean" value={formatSignedPercent(slice.rawMeanProfitPercent)} />
                <StatRow label="Median (raw)" value={formatSignedPercent(slice.medianProfitPercent)} />
                <StatRow label="Net profit sum" value={formatSignedPercent(slice.netProfitPercentSum)} />
            </StatList>
            <StatList>
                <StatRow label="Win rate" value={formatShare(slice.winRate, 1)} />
                <StatRow label="Profit factor" value={slice.profitFactor === null ? '—' : slice.profitFactor.toFixed(2)} />
                <StatRow label="p10 outcome" value={formatSignedPercent(slice.p10ProfitPercent)} />
                <StatRow
                    label="Distinct entities / dates"
                    value={`${formatInt(slice.distinctEntityCount)} / ${formatInt(slice.distinctEntryDateCount)}`}
                />
            </StatList>
            <StatList>
                {concentrationFlags.map((flag) => (
                    <StatRow
                        key={flag.key}
                        label={flag.label}
                        value={
                            flag.flagged ? (
                                <Tooltip content={`At or above the fragile threshold of ${formatShare(flag.threshold)}.`}>
                                    <span className="font-medium text-amber-400">
                                        <span aria-hidden>⚠︎ </span>
                                        <span className="sr-only">Above the fragile threshold of {formatShare(flag.threshold)}: </span>
                                        {formatNullableShare(flag.value)}
                                    </span>
                                </Tooltip>
                            ) : (
                                <span>{formatNullableShare(flag.value)}</span>
                            )
                        }
                    />
                ))}
            </StatList>
        </div>
    );
}

function MeanProfitBarCell({
    row,
    scale,
    withTradeShareBar
}: {
    row: PrimarySliceRow;
    scale: DivergingBarScale;
    withTradeShareBar: boolean;
}) {
    const segment = scale.segmentFor(row.meanProfitPercent);
    const barColor = row.belowEvaluationMinimum ? 'bg-zinc-600' : row.meanProfitPercent < 0 ? 'bg-red-500' : 'bg-green-500';

    // The 2px minimum keeps tiny values visible, but it must grow on the value's own side of the axis:
    // negatives anchor by their right edge so the clamp extends left, and an exact zero draws no bar.
    const barStyle =
        row.meanProfitPercent < 0
            ? { right: `${100 - scale.zeroPercent}%`, width: `max(${segment.widthPercent}%, 2px)` }
            : { left: `${scale.zeroPercent}%`, width: `max(${segment.widthPercent}%, 2px)` };

    return (
        <div className="flex items-center gap-3">
            <div className="relative h-5 min-w-32 flex-1">
                {/* zero axis */}
                <div aria-hidden className="absolute inset-y-0 w-px bg-zinc-500" style={{ left: `${scale.zeroPercent}%` }} />
                {row.meanProfitPercent !== 0 && <div className={`absolute top-0.5 h-2.5 rounded-sm ${barColor}`} style={barStyle} />}
                {withTradeShareBar && (
                    <div
                        aria-hidden
                        className="absolute bottom-0 left-0 h-1 rounded-sm bg-zinc-600"
                        style={{ width: `${row.tradeShare * 100}%` }}
                        title={`Trade share ${formatShare(row.tradeShare, 1)}`}
                    />
                )}
            </div>
            <span
                className={`w-18 shrink-0 text-right text-sm tabular-nums ${
                    row.belowEvaluationMinimum ? 'text-zinc-500' : row.meanProfitPercent < 0 ? 'text-red-400' : 'text-green-400'
                }`}
            >
                {formatSignedPercent(row.meanProfitPercent)}
            </span>
        </div>
    );
}

export interface RegimeSliceTableProps {
    dimension: RegimeProfileDimensionResult;
    thresholds: RegimeProfileThresholds;
    /** Primary panel only: show the opportunity-retention column. */
    showRetention?: boolean;
    /** Primary panel only: check the unclassified share against maxPrimaryUnclassifiedShare. */
    checkUnclassifiedShare?: boolean;
}

/**
 * Per-state slice table shared by the Layer 2 primary panel and the Layer 4 sub-dimension panels.
 * Sorted by mean profit; episode count is a full-weight chip next to the state name (design rule 1);
 * slices below the evaluation minimum grey out but are never dropped.
 */
export function RegimeSliceTable({ dimension, thresholds, showRetention = false, checkUnclassifiedShare = false }: RegimeSliceTableProps) {
    const rows = useMemo(() => buildPrimarySliceRows(dimension, thresholds), [dimension, thresholds]);
    const scale = useMemo(() => buildDivergingBarScale(rows.map((row) => row.meanProfitPercent)), [rows]);

    const totalTrades = dimension.classifiedTradeCount + dimension.unclassifiedTradeCount;
    const unclassifiedShare = totalTrades > 0 ? dimension.unclassifiedTradeCount / totalTrades : 0;
    const unclassifiedShareExceeded = checkUnclassifiedShare && unclassifiedShare > thresholds.maxPrimaryUnclassifiedShare;

    const columns = useMemo<ColumnDef<PrimarySliceRow>[]>(() => {
        const baseColumns: ColumnDef<PrimarySliceRow>[] = [
            {
                accessorKey: 'state',
                cell: ({ row }) => (
                    <div className={row.original.belowEvaluationMinimum ? 'text-zinc-500' : 'text-zinc-100'}>
                        <span className="font-medium">{row.original.state}</span>
                        {row.original.belowEvaluationMinimum && (
                            <div className="text-xs text-zinc-500">below evaluation minimum ({thresholds.minTradesPerSlice} trades)</div>
                        )}
                    </div>
                ),
                header: 'State',
                size: 190
            },
            {
                accessorKey: 'episodeCount',
                cell: ({ row }) => (
                    <span className="inline-flex items-center gap-1.5">
                        <span
                            className={`inline-flex items-center rounded px-2 py-0.5 text-sm font-semibold ${
                                row.original.episodeStarved
                                    ? 'border border-amber-700 bg-amber-900/40 text-amber-300'
                                    : 'border border-zinc-600 bg-zinc-700/60 text-zinc-100'
                            }`}
                        >
                            {formatInt(row.original.episodeCount)}
                        </span>
                        {row.original.episodeStarved && (
                            <Tooltip
                                content={`Episode-starved: fewer than ${thresholds.minEpisodesPerSlice} independent regime episodes back this slice, so its trade count overstates its evidence.`}
                            >
                                <span className="cursor-help text-amber-400">
                                    <span aria-hidden>⚠︎</span>
                                    <span className="sr-only">
                                        Episode-starved: fewer than {thresholds.minEpisodesPerSlice} independent episodes
                                    </span>
                                </span>
                            </Tooltip>
                        )}
                    </span>
                ),
                header: 'Episodes',
                size: 110
            },
            {
                accessorKey: 'meanProfitPercent',
                cell: ({ row }) => <MeanProfitBarCell row={row.original} scale={scale} withTradeShareBar />,
                header: 'Mean profit',
                minSize: 260,
                size: 340
            },
            {
                accessorKey: 'tradeCount',
                cell: ({ row }) => (
                    <span className={row.original.belowEvaluationMinimum ? 'text-zinc-500' : 'text-zinc-200'}>
                        {formatInt(row.original.tradeCount)}
                        <span className="ml-1 text-xs text-zinc-500">({formatShare(row.original.tradeShare, 1)})</span>
                    </span>
                ),
                header: 'Trades',
                size: 120
            }
        ];

        if (showRetention) {
            baseColumns.push({
                accessorKey: 'opportunityRetention',
                cell: ({ row }) =>
                    row.original.opportunityRetention === undefined ? (
                        <span className="text-zinc-500">—</span>
                    ) : (
                        <Tooltip
                            content={`Included trades / ${formatInt(row.original.opportunityCount ?? 0)} candidate opportunities whose point-in-time regime was this state.`}
                        >
                            <span className="cursor-help text-zinc-200">{formatShare(row.original.opportunityRetention)}</span>
                        </Tooltip>
                    ),
                header: 'Retention',
                size: 100
            });
        }

        return baseColumns;
    }, [scale, showRetention, thresholds.minEpisodesPerSlice, thresholds.minTradesPerSlice]);

    return (
        <div>
            <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500">
                <span>
                    {formatInt(dimension.classifiedTradeCount)} classified · {formatInt(dimension.unclassifiedTradeCount)} unclassified
                </span>
                {unclassifiedShareExceeded && (
                    <span className="font-medium text-amber-400">
                        ⚠︎ {formatShare(unclassifiedShare, 1)} unclassified exceeds the {formatShare(thresholds.maxPrimaryUnclassifiedShare)}{' '}
                        maximum
                    </span>
                )}
            </div>
            <ExpandableDataTable<PrimarySliceRow>
                columns={columns}
                data={rows}
                getRowId={(row) => row.state}
                initialSorting={[{ desc: false, id: 'meanProfitPercent' }]}
                renderSubComponent={({ row }: { row: Row<PrimarySliceRow> }) => (
                    <SliceDetail slice={row.original} thresholds={thresholds} />
                )}
                search={false}
                striped={false}
            />
        </div>
    );
}
